// ===============================================================
// ProBank — Loan App Payment (Premium/Elite, subscribed)
//
// Pay into a loan app's collection account. Smart loan-app picker:
// search the built-in list, OR type any name that's not listed and
// the dropdown lets you use it directly — no dead ends. Live
// account verification, TRANSACTION PIN authorization, then
// processing → clearing → result.
//
// ORDER OF CHECKS (like real banks):
//   1. Insufficient balance → ALWAYS shown first, before anything
//   2. Delivery gate (no subscription + no card) → warning, only
//      when the user can actually afford the payment
//   3. PIN → payment
// ===============================================================

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import { useCountry } from "../context/CountryContext";
import PinPad from "../components/PinPad";
import { fetchDeliveryGate, DeliveryGateModal } from "../components/DeliveryGate";

const API_BASE = "https://alkebulanfkalertbackend.onrender.com/api";

// 🎨 Per-wallet brand skins
const BRAND = {
  opay: {
    label: "ProBank Wallet", emoji: "💚",
    bg: "#04100a", card: "#0a1f14", line: "#13402850",
    accent: "#34d399", accentSoft: "#34d39914", accentText: "#04100a",
  },
  palmpay: {
    label: "PalmPay", emoji: "💜",
    bg: "#0d0514", card: "#1a0f26", line: "#6d28d950",
    accent: "#c084fc", accentSoft: "#c084fc14", accentText: "#1a0f26",
  },
  kuda: {
    label: "Kuda", emoji: "💜",
    bg: "#0b0713", card: "#170f26", line: "#8b5cf650",
    accent: "#a78bfa", accentSoft: "#a78bfa14", accentText: "#170f26",
  },
  moniepoint: {
    label: "Moniepoint", emoji: "💙",
    bg: "#040d16", card: "#0a1a2a", line: "#38bdf850",
    accent: "#38bdf8", accentSoft: "#38bdf814", accentText: "#040d16",
  },
};

// ---------------------------------------------------------------
// Loan apps — comprehensive list per country. Users can also type
// any name not in this list; the picker lets them use it directly.
// ---------------------------------------------------------------
const LOAN_APPS = {
  NG: [
    "FairMoney",
    "Carbon (Paylater)",
    "Palmcredit",
    "Branch",
    "Renmoney",
    "QuickCheck",
    "OKash",
    "Aella Credit",
    "Umba",
    "Newedge (EasyBuy)",
    "Kuda Overdraft",
    "OPay Okash Loan",
    "PalmPay Flexi",
    "Moniepoint Working Capital",
    "Kredi Money",
    "Kiakia",
    "Page Financials",
    "CreditDirect",
    "Migo (Kwikmoney)",
    "Specta (Sterling)",
    "Alat Loans (Wema)",
    "Access PayDay Loan",
    "GTBank QuickCredit",
    "Zenith Eazy Loan",
    "UBA Click Credit",
    "FirstMonie Loan",
    "Stanbic EZ Cash",
    "FCMB Fast Cash",
    "Union Bank UnionPlus",
    "Fidelity Fast Loan",
    "MoneyPal",
    "Payhippo",
    "Lendigo",
    "PayCentre Capital",
    "Sparkle Microfinance",
    "VFD Microfinance (V Bank)",
    "Mint Digital Bank Loan",
    "Rubies Bank Credit",
    "Eyowo Capital",
    "Sycamore",
    "CashX",
    "iMoney (iMoneyPay)",
    "NairaPlus",
    "TrueNaira",
    "NaijaLoan",
    "LairaPlus",
    "GoCash",
    "EaseMoni",
    "SwiftKash",
    "LoanBoss",
    "Zedvance",
    "Rosabon Financial",
    "Creditville",
    "Advancly",
    "Payhippo SME",
    "Moni (formerly TeamApt Credit)",
    "TradeDepot Credit",
    "Pivo Capital",
    "Money In Minutes",
    "Fint",
    "GetCash",
    // More apps users commonly ask about
    "9Credit",
    "9jaCash",
    "Aella App",
    "Baobab Microfinance",
    "Blocks (payday)",
    "Carbon Zero",
    "CashBus",
    "CashLion",
    "CreditClan",
    "Credpal",
    "EasyCredit",
    "FairCredit",
    "Grooming Centre",
    "Irorun",
    "JollofCash",
    "Klump",
    "LAPO Microfinance",
    "Lendsqr",
    "LetMeBorrow",
    "LoanInMinutes",
    "MoneyPal Credit",
    "NaijaCash",
    "NairaLoan",
    "Nirvana Credit",
    "OKash Plus",
    "OneFinance",
    "OxMoni",
    "Oxygen Loan",
    "PalmCash",
    "Payhippo Capital",
    "Payhippo Loan",
    "QuickCredit",
    "QuickLoan NG",
    "Renmoney Plus",
    "SureCredit",
    "SunTrust Loan",
    "Truq Credit",
    "V Bank Loan",
    "XCredit",
    "Zedvance Express",
    "Accion Microfinance",
    "AB Microfinance",
    "Other Loan App",
  ],
  GH: [
    "Fido",
    "Carbon Ghana",
    "MTN QwikLoan",
    "MTN Ahomka Loan",
    "Telecel Cash Loan",
    "AirtelTigo Money Loan",
    "Zeepay Loan",
    "Oze",
    "Paylater GH",
    "Pezesha Ghana",
    "Izwe Loans",
    "Bayport Ghana",
    "Letshego Ghana",
    "CashPlus Ghana",
    "ExpressCredit Ghana",
    "Daakye Microfinance",
    "Sinapi Aba",
    "Advans Ghana",
    "Opportunity International",
    // More Ghana options
    "Branch Ghana",
    "Jumo Ghana",
    "Mazzuma Credit",
    "MTN Ahomka Plus",
    "Oze Business Loan",
    "Susu Digital Ghana",
    "Other Loan App",
  ],
};

export default function LoanPaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isGhana, config } = useCountry();

  const cur = config?.currencySymbol || (isGhana ? "GH₵" : "₦");
  const countryCode = isGhana ? "GH" : "NG";
  const brand = BRAND[location.state?.from] || BRAND.opay;

  const [user, setUser] = useState(null);
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [accountNumber, setAccountNumber] = useState("");
  const [resolvedName, setResolvedName] = useState("");
  const [resolving, setResolving] = useState(false);
  const [selectedLoanApp, setSelectedLoanApp] = useState(null);
  const [customLoanApp, setCustomLoanApp] = useState("");
  const [amount, setAmount] = useState("");
  const [recents, setRecents] = useState([]);
  const [stage, setStage] = useState("form"); // form | confirm | processing | clearing | done
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [blocker, setBlocker] = useState(null);
  const [pinOpen, setPinOpen] = useState(false);

  // Delivery gate — checked on mount, enforced before the PIN pad
  const [canDeliver, setCanDeliver] = useState(true);
  const [gateOpen, setGateOpen] = useState(false);

  useEffect(() => {
    try {
      setUser(JSON.parse(localStorage.getItem("user") || "{}"));
    } catch {}
  }, []);

  // Upfront gate check — no subscription + no card → warn first
  useEffect(() => {
    fetchDeliveryGate().then(({ canDeliver }) => setCanDeliver(canDeliver));
  }, []);

  // Recent loan payments
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    fetch(`${API_BASE}/bank/recipients?type=loan`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setRecents(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Real bank list — same call as TransferToBankPage
  useEffect(() => {
    const fetchBanks = async () => {
      setBanksLoading(true);
      try {
        const res = await fetch(`${API_BASE}/bank/list?country=${countryCode}`);
        const data = await res.json();
        setBanks(
          (Array.isArray(data) ? data : []).map((b) => ({
            value: b.code,
            label: b.name,
          }))
        );
      } catch {
        setBanks([]);
      } finally {
        setBanksLoading(false);
      }
    };
    fetchBanks();
  }, [countryCode]);

  // Live account-name verification
  useEffect(() => {
    const resolveAccount = async () => {
      if (accountNumber.length === 10 && selectedBank?.value) {
        setResolving(true);
        setResolvedName("");
        try {
          const res = await fetch(`${API_BASE}/bank/resolve`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              account_number: accountNumber,
              bank_code: selectedBank.value,
              country: countryCode,
            }),
          });
          const data = await res.json();
          const name = data?.account_name || data?.data?.account_name;
          setResolvedName(name || "❌ Could not verify account");
        } catch {
          setResolvedName("⚠️ Error verifying account");
        } finally {
          setResolving(false);
        }
      } else {
        setResolvedName("");
      }
    };
    resolveAccount();
  }, [accountNumber, selectedBank, countryCode]);

  const fmt = (n) =>
    Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const nameOk = resolvedName && !resolvedName.startsWith("❌") && !resolvedName.startsWith("⚠️");

  // The final loan app name. With CreatableSelect, a typed name that
  // isn't in the list becomes the value directly. "Other Loan App"
  // (chosen via the manual link) uses the free-text field instead.
  const isOther = selectedLoanApp?.value === "Other Loan App";
  const loanAppName = isOther ? customLoanApp.trim() : (selectedLoanApp?.value || "");

  const pickRecent = (r) => {
    setAccountNumber(r.accountNumber || "");
    if (r.bankName) {
      const app = (LOAN_APPS[countryCode] || []).find(
        (a) => a.toLowerCase() === String(r.bankName).toLowerCase()
      );
      if (app) {
        setSelectedLoanApp({ value: app, label: `📱 ${app}` });
        setCustomLoanApp("");
      } else {
        setSelectedLoanApp({ value: "Other Loan App", label: "📱 Other Loan App" });
        setCustomLoanApp(r.bankName);
      }
    }
    if (r.bankCode) {
      const match = banks.find((b) => b.value === r.bankCode);
      if (match) setSelectedBank(match);
    }
  };

  // Balance check — ALWAYS evaluated before any gate warning
  const hasInsufficientFunds = () => Number(amount) > Number(user?.balance || 0);

  const insufficientMessage = () =>
    `This payment of ${cur}${fmt(amount)} is more than your available balance of ${cur}${fmt(user?.balance)}. Please fund your wallet first.`;

  const validate = () => {
    if (!selectedLoanApp) return "Select the loan app you are paying.";
    if (isOther && !customLoanApp.trim()) return "Type the name of the loan app.";
    if (!selectedBank) return "Select the loan app's collection bank.";
    if (accountNumber.length !== 10) return "Enter the 10-digit collection account number.";
    if (!nameOk) return "The collection account could not be verified.";
    if (!Number(amount) || Number(amount) <= 0) return "Enter a valid amount.";
    return null;
  };

  const handleSubmit = () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError("");

    // Balance check first — before the confirm screen even opens
    if (hasInsufficientFunds()) {
      setBlocker({
        icon: "💳",
        title: "Insufficient Balance",
        message: insufficientMessage(),
        action: { label: "Fund Wallet", path: "/purchase" },
      });
      return;
    }

    setStage("confirm");
  };

  // Called by PinPad with the entered 4-digit PIN
  const submitPayment = async (pin) => {
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(`${API_BASE}/bank/wallet/loan`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          amount: Number(amount),
          recipient_name: resolvedName,
          loan_app_name: loanAppName,
          account_reference: accountNumber,
          bank_code: selectedBank.value,
          bank_name: selectedBank.label,
          pin,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        // PIN errors are handled inside the PinPad itself
        if (["PIN_INCORRECT", "PIN_LOCKED", "PIN_NOT_SET", "PIN_REQUIRED"].includes(data.code)) {
          return { ok: false, data };
        }

        setStage("clearing");
        setTimeout(() => {
          if (data.code === "INSUFFICIENT_BALANCE") {
            setBlocker({ icon: "💳", title: "Insufficient Balance", message: data.error, action: { label: "Purchase Coins", path: "/purchase" } });
          } else if (data.code === "PLAN_UPGRADE_REQUIRED") {
            setBlocker({ icon: "⭐", title: "Premium Plan Required", message: data.error, action: { label: "Subscribe Now", path: "/subscription" } });
          } else if (data.code === "SUBSCRIPTION_PENDING") {
            setBlocker({ icon: "⏳", title: "Subscription Pending Approval", message: data.error, action: { label: "My Subscription", path: "/subscription" } });
          } else if (data.code === "ACCOUNT_LOCKED") {
            setBlocker({ icon: "🔒", title: "Account Locked", message: data.error });
          } else {
            setError(data.error || "Transaction failed.");
          }
          setStage("form");
        }, 1600);
        return { ok: false, data };
      }

      const tx = data.transaction || {};
      tx.delivered = data.delivered !== false;
      setResult(tx);

      try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        u.balance = tx.new_balance;
        localStorage.setItem("user", JSON.stringify(u));
        setUser(u);
      } catch {}

      setStage("processing");
      setTimeout(() => setStage("clearing"), 1000);
      setTimeout(() => setStage("done"), 2600);
      return { ok: true, data };
    } catch {
      setError("Network error. Please try again.");
      setStage("form");
      return { ok: false, data: {} };
    }
  };

  // Confirm & Pay → balance first, then gate, then PIN
  const handleConfirm = () => {
    // Balance check ALWAYS comes first — before the gate warning
    if (hasInsufficientFunds()) {
      setBlocker({
        icon: "💳",
        title: "Insufficient Balance",
        message: insufficientMessage(),
        action: { label: "Fund Wallet", path: "/purchase" },
      });
      return;
    }

    // Gate only when funds are sufficient
    if (!canDeliver) {
      setGateOpen(true);
      return;
    }

    setPinOpen(true);
  };

  const inputStyle = {
    width: "100%", padding: "13px 14px", borderRadius: 10,
    border: `1px solid ${brand.line}`, background: brand.bg,
    color: "#f4f6f8", fontSize: 14, outline: "none", boxSizing: "border-box",
  };

  const labelStyle = { display: "block", fontSize: 12, color: "#7a93ab", marginBottom: 6, fontWeight: 600 };

  const selectStyles = {
    control: (base) => ({
      ...base, backgroundColor: brand.bg, border: `1px solid ${brand.line}`,
      borderRadius: 10, padding: "3px", boxShadow: "none",
    }),
    singleValue: (base) => ({ ...base, color: "#f4f6f8" }),
    input: (base) => ({ ...base, color: "#f4f6f8" }),
    menu: (base) => ({ ...base, backgroundColor: brand.card, zIndex: 30 }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? brand.accentSoft : brand.card,
      color: "#f4f6f8",
    }),
    placeholder: (base) => ({ ...base, color: "#7a93ab" }),
  };

  // Same dark styles, but creatable rows get a subtle highlight so
  // "Use 'xyz'" stands out from the built-in list.
  const creatableStyles = {
    ...selectStyles,
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? brand.accentSoft : brand.card,
      color: state.data.__isNew__ ? brand.accent : "#f4f6f8",
      fontWeight: state.data.__isNew__ ? 700 : 400,
    }),
  };

  const loanAppOptions = (LOAN_APPS[countryCode] || LOAN_APPS.NG).map((a) => ({
    value: a,
    label: `📱 ${a}`,
  }));

  // When the user picks or creates a loan app
  const handleLoanAppChange = (opt, actionMeta) => {
    if (!opt) {
      setSelectedLoanApp(null);
      setCustomLoanApp("");
      return;
    }

    // Created from free typing → the typed name IS the loan app.
    if (actionMeta?.action === "create-option" || opt.__isNew__) {
      const typed = String(opt.value || "").trim();
      if (!typed) return;
      setSelectedLoanApp({ value: typed, label: `📱 ${typed}` });
      setCustomLoanApp("");
      return;
    }

    setSelectedLoanApp(opt);
    if (opt.value !== "Other Loan App") setCustomLoanApp("");
  };

  return (
    <div style={{ minHeight: "100vh", background: brand.bg, color: "#f4f6f8", fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* Header */}
      <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 14, borderBottom: `1px solid ${brand.line}`, position: "sticky", top: 0, background: brand.bg, zIndex: 10 }}>
        <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", color: "#f4f6f8", fontSize: 20, cursor: "pointer", padding: 4 }}>←</button>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Loan App Payment</div>
          <div style={{ fontSize: 11, color: "#7a93ab" }}>Available balance: {cur}{fmt(user?.balance)}</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: brand.accent, fontWeight: 600 }}>{brand.emoji} {brand.label}</span>
          <span style={{ fontSize: 10, padding: "4px 10px", borderRadius: 20, background: brand.accentSoft, color: brand.accent, fontWeight: 700 }}>
            PREMIUM / ELITE
          </span>
        </div>
      </div>

      <div style={{ maxWidth: 460, margin: "0 auto", padding: "20px 20px 60px" }}>
        {stage === "form" && (
          <>
            {recents.length > 0 && (
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 12, color: "#7a93ab", fontWeight: 600, marginBottom: 10 }}>RECENT LOAN PAYMENTS</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {recents.map((r, i) => (
                    <button key={i} onClick={() => pickRecent(r)}
                      style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", background: brand.card, border: `1px solid ${brand.line}`, borderRadius: 10, cursor: "pointer", textAlign: "left" }}>
                      <div style={{ width: 34, height: 34, borderRadius: 8, background: brand.accentSoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>📱</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: "#f4f6f8", fontSize: 13, fontWeight: 600 }}>{r.name}</div>
                        <div style={{ color: "#7a93ab", fontSize: 11 }}>{r.bankName} · {r.accountNumber}</div>
                      </div>
                      <span style={{ color: "#7a93ab", fontSize: 14 }}>›</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>LOAN APP</label>
              <CreatableSelect
                options={loanAppOptions}
                value={selectedLoanApp}
                onChange={handleLoanAppChange}
                isSearchable
                isClearable
                placeholder="Search or type any loan app..."
                styles={creatableStyles}
                formatCreateLabel={(input) => `✚ Use "${input}"`}
                noOptionsMessage={() => "Type the loan app name to add it"}
                createOptionPosition="first"
              />
              <div style={{ fontSize: 11, color: "#7a93ab", marginTop: 6, lineHeight: 1.5 }}>
                Not listed? Just type the name and tap{" "}
                <span style={{ color: brand.accent, fontWeight: 600 }}>✚ Use "..."</span>{" "}
                in the dropdown, or{" "}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedLoanApp({ value: "Other Loan App", label: "📱 Other Loan App" });
                    setCustomLoanApp("");
                  }}
                  style={{
                    background: "none", border: "none", padding: 0,
                    color: brand.accent, fontSize: 11, fontWeight: 600, cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  add it manually
                </button>
                .
              </div>
            </div>

            {/* Free-text field when their loan app isn't in the list */}
            {isOther && (
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>LOAN APP NAME</label>
                <input value={customLoanApp} onChange={(e) => setCustomLoanApp(e.target.value)} placeholder="Type the loan app name" style={inputStyle} />
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>COLLECTION BANK</label>
              <Select
                options={banks}
                value={selectedBank}
                onChange={setSelectedBank}
                isSearchable
                isLoading={banksLoading}
                placeholder={banksLoading ? "Loading banks..." : "Bank the loan app gave you..."}
                styles={selectStyles}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>COLLECTION ACCOUNT NUMBER</label>
              <input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit account from the loan app" inputMode="numeric" style={inputStyle} />
              {resolving && (
                <div style={{ color: brand.accent, fontSize: 12, marginTop: 6 }}>🔄 Verifying account...</div>
              )}
              {resolvedName && !resolving && (
                <div style={{
                  marginTop: 6, fontSize: 12, padding: "7px 12px", borderRadius: 8,
                  background: nameOk ? "#22c55e1a" : "#ef44441a",
                  color: nameOk ? "#4ade80" : "#f87171",
                  border: `1px solid ${nameOk ? "#22c55e40" : "#ef444440"}`,
                }}>
                  {nameOk ? `👤 ${resolvedName}` : resolvedName}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>AMOUNT ({cur})</label>
              <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))} placeholder="0.00" inputMode="decimal" style={{ ...inputStyle, fontSize: 18, fontWeight: 700 }} />
            </div>

            {error && <div style={{ color: "#f87171", fontSize: 13, margin: "10px 0" }}>{error}</div>}

            <button onClick={handleSubmit}
              style={{ width: "100%", marginTop: 14, padding: "14px", borderRadius: 10, border: "none", background: brand.accent, color: brand.accentText, fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
              Review Payment
            </button>
          </>
        )}

        {stage === "confirm" && (
          <div style={{ background: brand.card, border: `1px solid ${brand.line}`, borderRadius: 14, overflow: "hidden" }}>
            <div style={{ padding: "18px 20px", borderBottom: `1px solid ${brand.line}` }}>
              <div style={{ fontSize: 12, color: "#7a93ab", fontWeight: 600 }}>CONFIRM LOAN PAYMENT</div>
            </div>
            <div style={{ padding: "20px" }}>
              <div style={{ textAlign: "center", marginBottom: 22 }}>
                <div style={{ fontSize: 11, color: "#7a93ab", marginBottom: 4 }}>You are paying</div>
                <div style={{ fontSize: 32, fontWeight: 800 }}>{cur}{fmt(amount)}</div>
              </div>
              {[
                ["Loan App", loanAppName],
                ["Account Name", resolvedName],
                ["Bank", selectedBank?.label || ""],
                ["Account Number", accountNumber],
              ].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderTop: `1px solid ${brand.line}` }}>
                  <span style={{ color: "#7a93ab", fontSize: 13 }}>{k}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, textAlign: "right", maxWidth: "60%", wordBreak: "break-word" }}>{v}</span>
                </div>
              ))}
              <button onClick={handleConfirm}
                style={{ width: "100%", marginTop: 20, padding: "14px", borderRadius: 10, border: "none", background: brand.accent, color: brand.accentText, fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
                Confirm &amp; Pay
              </button>
              <button onClick={() => setStage("form")}
                style={{ width: "100%", marginTop: 10, padding: "12px", borderRadius: 10, border: `1px solid ${brand.line}`, background: "none", color: "#7a93ab", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
                Back
              </button>
            </div>
          </div>
        )}

        {(stage === "processing" || stage === "clearing") && (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <div style={{ width: 40, height: 40, margin: "0 auto 18px", border: `3px solid ${brand.line}`, borderTopColor: brand.accent, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              {stage === "processing" ? "Processing payment…" : "Loan clearing…"}
            </div>
            <div style={{ fontSize: 12, color: "#7a93ab", marginTop: 6 }}>
              {stage === "processing"
                ? "Submitting to the payment network. Do not close this page."
                : "Verifying loan account and clearing funds."}
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {stage === "done" && result && (
          <div style={{ background: brand.card, border: `1px solid ${brand.line}`, borderRadius: 14, overflow: "hidden" }}>
            <div style={{ padding: "24px 20px", textAlign: "center", borderBottom: `1px solid ${brand.line}` }}>
              <div style={{
                width: 52, height: 52, margin: "0 auto 12px", borderRadius: "50%",
                background: result.delivered ? "#dcfce722" : "#f59e0b22",
                border: result.delivered ? "1px solid #22c55e44" : "1px solid #f59e0b44",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 24, color: result.delivered ? "#22c55e" : "#fbbf24",
              }}>
                {result.delivered ? "✓" : "⏳"}
              </div>
              <div style={{ fontWeight: 700, fontSize: 17 }}>
                {result.delivered ? "Payment Successful" : "Payment Recorded"}
              </div>
              <div style={{ color: "#7a93ab", fontSize: 12, marginTop: 4 }}>
                {result.delivered ? "Loan payment completed" : "Held — pending subscription or card"}
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, marginTop: 14 }}>{cur}{fmt(result.amount)}</div>
            </div>
            <div style={{ padding: "16px 20px" }}>
              {[
                ["Loan App", loanAppName],
                ["Account Name", resolvedName],
                ["Bank", selectedBank?.label || ""],
                ["Account Number", accountNumber],
                ["Transaction ID", result.transactionId || ""],
                ["New Balance", `${cur}${fmt(result.new_balance)}`],
                ["Status", result.delivered ? "Successful" : "Pending — not yet with recipient"],
              ].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: `1px solid ${brand.line}` }}>
                  <span style={{ color: "#7a93ab", fontSize: 12 }}>{k}</span>
                  <span style={{
                    fontSize: 12, fontWeight: 600, textAlign: "right", maxWidth: "60%", wordBreak: "break-all",
                    ...(k === "Status" && !result.delivered ? { color: "#fbbf24" } : {}),
                  }}>{v}</span>
                </div>
              ))}

              {!result.delivered && (
                <div style={{
                  marginTop: 14, padding: "12px 14px", borderRadius: 10,
                  background: "#f59e0b1a", border: "1px solid #f59e0b40",
                  color: "#fbbf24", fontSize: 12, lineHeight: 1.6,
                }}>
                  This transaction is recorded on your account, but it will not reflect
                  with the loan app until you subscribe to a plan or activate a ProBank card.
                </div>
              )}

              <button onClick={() => navigate("/dashboard")}
                style={{ width: "100%", marginTop: 18, padding: "14px", borderRadius: 10, border: "none", background: brand.accent, color: brand.accentText, fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
                Back to Dashboard
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Pre-transfer delivery warning (only when gated) */}
      <DeliveryGateModal
        open={gateOpen}
        brand={brand}
        onContinue={() => {
          setGateOpen(false);
          setPinOpen(true);
        }}
        onCancel={() => setGateOpen(false)}
      />

      {/* Transaction PIN pad */}
      <PinPad
        open={pinOpen}
        onClose={() => setPinOpen(false)}
        onSuccess={submitPayment}
        title="Authorize Loan Payment"
        subtitle={`${cur}${fmt(amount)} to ${loanAppName || "loan app"}`}
        brand={{ accent: brand.accent, accentText: brand.accentText }}
      />

      {/* Blocker modal */}
      {blocker && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }}>
          <div style={{ background: brand.card, border: `1px solid ${brand.line}`, borderRadius: 14, padding: 26, maxWidth: 360, width: "100%", textAlign: "center" }}>
            <div style={{ fontSize: 34, marginBottom: 12 }}>{blocker.icon}</div>
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 8 }}>{blocker.title}</div>
            <div style={{ color: "#7a93ab", fontSize: 13, lineHeight: 1.6, marginBottom: 18 }}>{blocker.message}</div>
            {blocker.action && (
              <button onClick={() => navigate(blocker.action.path)}
                style={{ width: "100%", padding: "12px", borderRadius: 10, border: "none", background: brand.accent, color: brand.accentText, fontWeight: 700, cursor: "pointer", marginBottom: 10 }}>
                {blocker.action.label}
              </button>
            )}
            <button onClick={() => setBlocker(null)}
              style={{ width: "100%", padding: "11px", borderRadius: 10, border: `1px solid ${brand.line}`, background: "none", color: "#7a93ab", fontWeight: 600, cursor: "pointer" }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
